import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getLastNDaysDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
  getThisMonthDateRange,
} from 'src/utils/dateUtils';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
  getLookbackThisMonthBlockedMessage,
} from 'src/utils/lookbackMessages';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import {
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_SHOPIFY_METRICS,
  GET_SHOPIFY_METRICS_VALUES,
  GET_MAGENTO_METRICS,
  GET_MAGENTO_METRICS_VALUES,
  GET_GOOGLE_ANALYTICS_METRICS,
  GET_CUSTOM_FORMULAS,
  GET_KPI_SECTIONS,
} from 'src/utils/gql/queries';
import {
  CREATE_KPI_SECTION,
  UPDATE_KPI_SECTION,
  DELETE_KPI_SECTION,
  REORDER_KPI_SECTIONS,
  GENERATE_KPI_AI_REPORT,
  GENERATE_KPI_AI_REPORT_DATA,
} from 'src/utils/gql/mutations';
import { GET_INVENTORY_COUNTRY_OPTIONS } from 'src/utils/gql/inventoryQueries';
import {
  ConnectorType,
  Metric,
  MetricValuesRow,
  MetricsByConnectorType,
  KpiSection,
  ValueType,
} from 'src/utils/gql/types';
import { KPICard } from './components/KPICard';
import { MetricSelector } from './components/MetricSelector';
import { useDragAndDrop } from 'src/hooks/useDragAndDrop';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import type { KpiMetricTarget, SelectedMetric } from './types';
import { resolveKpiSectionToUpdate } from './utils/resolveKpiSectionToUpdate';
import { buildKpiFilters } from './utils/buildKpiFilters';
import { parseKpiFiltersConfig } from './utils/parseKpiFiltersConfig';
import {
  nextKpiSectionSortOrder,
  orderKpiSections,
} from './utils/orderKpiSections';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';

type KpiQuickSaveOptions = {
  /**
   * Salvataggi "silenziosi" (es. target KPI): niente toast di successo e niente busy globale,
   * ma manteniamo comunque la persistenza server + refetch liste.
   */
  silent?: boolean;
};

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(2.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginTop: '60px',
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    // Evita overflow orizzontale: `100vw` include anche scrollbar/offset e, sommato al marginLeft,
    // può tagliare contenuti a destra (es. CTA in header).
    width: 'calc(100% - 240px)',
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const DateSelectorSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
  padding: theme.spacing(1.5),
  backgroundColor: 'white',
  borderRadius: theme.spacing(1),
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(1.5),
}));

const ControlsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: 'white',
  borderRadius: theme.spacing(1),
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const KPIGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const KPI_STORAGE_KEY = 'kpi_panel_state';
const KPI_SECTION_ID_KEY = 'kpi_section_id';
/** Cache locale ottimistica; la source of truth è `config.sortOrder` sul server. */
const KPI_SECTIONS_ORDER_KEY = 'kpi_sections_order';

const KPI: React.FC = () => {
  const snackbar = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const draggedSectionIdRef = useRef<string | null>(null);
  const sectionDragClickGuardRef = useRef(false);
  /** Evita che un refetch intermedio ripristini l'ordine API durante il drag/save. */
  const pendingSectionOrderRef = useRef<string[] | null>(null);
  const prevLocationRef = useRef<{ pathname: string; search: string } | null>(
    null,
  );

  // Funzione helper per salvare lo stato in localStorage
  const saveStateToStorage = useCallback(
    (state: {
      selectedMetrics: SelectedMetric[];
      dateRange: { from: string; to: string };
      dateRangePreset: string;
      showCharts: boolean;
      columnOrder: string[];
    }) => {
      try {
        localStorage.setItem(KPI_STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving KPI state to localStorage:', error);
      }
    },
    [],
  );

  // Funzione helper per caricare lo stato da localStorage
  const loadStateFromStorage = useCallback((): {
    selectedMetrics: SelectedMetric[];
    dateRange: { from: string; to: string };
    dateRangePreset: string;
    showCharts: boolean;
    columnOrder: string[];
  } | null => {
    try {
      const stored = localStorage.getItem(KPI_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading KPI state from localStorage:', error);
    }
    return null;
  }, []);

  // Carica stato iniziale da localStorage o usa default
  const savedState = loadStateFromStorage();

  // Helper per calcolare il dateRange corretto in base al preset
  const getInitialDateRange = () => {
    if (!savedState?.dateRangePreset) {
      return getLastNDaysDateRange(30);
    }

    // Se il preset è dinamico, ricalcola sempre in base alla data corrente
    if (savedState.dateRangePreset === 'this_month') {
      return getThisMonthDateRange();
    } else if (savedState.dateRangePreset !== 'custom') {
      // Per "last N days", ricalcola sempre
      const days = parseInt(savedState.dateRangePreset, 10);
      if (!isNaN(days)) {
        return getLastNDaysDateRange(days);
      }
    }

    // Per preset "custom", usa le date salvate
    return savedState.dateRange || getLastNDaysDateRange(30);
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [dateRangePreset, setDateRangePreset] = useState<string>(
    savedState?.dateRangePreset || '30',
  );
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [compareMode, setCompareMode] = useState<string>('none'); // 'none' | 'previous_period' | 'previous_year' | 'custom'
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetric[]>(
    savedState?.selectedMetrics || [],
  );
  const [showMetricSelector, setShowMetricSelector] = useState(false);
  const [showCharts, setShowCharts] = useState(
    savedState?.showCharts !== undefined ? savedState.showCharts : true,
  );
  const [sectionsDrawerOpen, setSectionsDrawerOpen] = useState(false);
  const [saveSectionDialogOpen, setSaveSectionDialogOpen] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [selectedSection, setSelectedSection] = useState<KpiSection | null>(
    null,
  );
  const [lastKnownSectionId, setLastKnownSectionId] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(KPI_SECTION_ID_KEY);
      } catch {
        return null;
      }
    },
  );
  const [isSavingSection, setIsSavingSection] = useState(false);
  const [isRenamingSection, setIsRenamingSection] = useState(false);
  const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);
  const saveSeqRef = useRef(0);
  const [renameSectionDialogOpen, setRenameSectionDialogOpen] = useState(false);
  const [sectionPendingRename, setSectionPendingRename] =
    useState<KpiSection | null>(null);
  const [renameNameDraft, setRenameNameDraft] = useState('');
  const [sectionOrder, setSectionOrder] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(KPI_SECTIONS_ORDER_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const [sectionDropTargetId, setSectionDropTargetId] = useState<string | null>(
    null,
  );

  // Filtri sezione (stessi criteri Inventory: Shopify/Kipify compatibili)
  const [kpiCountryFilter, setKpiCountryFilter] = useState<string[]>([]);
  const [kpiCustomerTypeFilter, setKpiCustomerTypeFilter] =
    useState<string>('');
  const [kpiTagFilter, setKpiTagFilter] = useState<string[]>([]);
  const [kpiCollectionFilter, setKpiCollectionFilter] = useState<string[]>([]);
  const [kpiVendorFilter, setKpiVendorFilter] = useState<string[]>([]);
  const [kpiProductTypeFilter, setKpiProductTypeFilter] = useState<string[]>(
    [],
  );
  const [kpiShowMoreFilters, setKpiShowMoreFilters] = useState(false);

  const kpiFilters = useMemo(
    () =>
      buildKpiFilters({
        countries: kpiCountryFilter,
        customerType: kpiCustomerTypeFilter,
        tags: kpiTagFilter,
        collections: kpiCollectionFilter,
        vendors: kpiVendorFilter,
        productTypes: kpiProductTypeFilter,
      }),
    [
      kpiCountryFilter,
      kpiCustomerTypeFilter,
      kpiTagFilter,
      kpiCollectionFilter,
      kpiVendorFilter,
      kpiProductTypeFilter,
    ],
  );

  const kpiActiveFilterCount =
    kpiCountryFilter.length +
    (kpiCustomerTypeFilter ? 1 : 0) +
    kpiTagFilter.length +
    kpiCollectionFilter.length +
    kpiVendorFilter.length +
    kpiProductTypeFilter.length;

  // "Home" KPI: griglia sezioni (nessuna sezione attiva / nessuna metrica in editing).
  // In questa vista i controlli sopra (date range, add metric, ecc.) sono fuorvianti perché
  // non hanno contesto: mostriamo solo CTA "New Section".
  const isKpiHomeView =
    !selectedSection &&
    !lastKnownSectionId &&
    !isCreatingNewSection &&
    selectedMetrics.length === 0;

  // AI Report state
  const {
    aiCallsUsed,
    aiCallsLimit,
    lookbackDays,
    refetch: refetchTrialStatus,
  } = useTrialStatus();
  const aiCallsRemaining = aiCallsLimit - aiCallsUsed;
  const [aiReport, setAiReport] = useState<string | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [generateAIReport] = useMutation(GENERATE_KPI_AI_REPORT);
  const [generateAIReportData] = useMutation(GENERATE_KPI_AI_REPORT_DATA);

  const formatDateLabel = useCallback((value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }, []);

  const formatRangeLabel = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range?.from || !range?.to) return '';
      return `${formatDateLabel(range.from)} - ${formatDateLabel(range.to)}`;
    },
    [formatDateLabel],
  );

  const {
    googleAdsConnectorId,
    shopifyConnectorId,
    magentoConnectorId,
    magentoConnectorEnabled,
    facebookConnectorId,
    googleAnalytics4ConnectorId,
    clientAccountId,
    propertyId,
    kipifyMetrics,
    databaseConnectorId,
    databaseMetrics,
  } = useConnectors() as any;

  // Query opzioni filtri Shopify (tag, collection, vendor, product_type) per popolare i dropdown
  const inSectionView =
    (selectedSection || lastKnownSectionId || isCreatingNewSection) &&
    selectedMetrics.length > 0;
  const hasShopifyMetric = selectedMetrics.some(
    (m) => m.connectorType === ConnectorType.SHOPIFY,
  );
  const hasMagentoMetric = selectedMetrics.some(
    (m) => m.connectorType === ConnectorType.MAGENTO,
  );
  /** Markup è Kipify ma i filtri (ordini commerce) devono restare disponibili anche senza card Shopify/Magento */
  const hasMarkupKipifyMetric = selectedMetrics.some(
    (m) =>
      m.connectorType === MetricsByConnectorType.KIPIFY &&
      m.metric?.key === 'markup',
  );
  const shouldLoadKpiFilterOptions =
    hasShopifyMetric || hasMagentoMetric || hasMarkupKipifyMetric;

  const { data: kpiShopifyFiltersData } = useQuery(GET_SHOPIFY_METRICS_VALUES, {
    skip: !shopifyConnectorId || !inSectionView || !shouldLoadKpiFilterOptions,
    variables: {
      args: {
        metricKeys: ['total_sales'],
        splitsKeys: [],
        filters: [],
        dateRange: { from: dateRange.from, to: dateRange.to },
      },
    },
    fetchPolicy: 'cache-first',
  });

  const { data: kpiMagentoFiltersData } = useQuery(GET_MAGENTO_METRICS_VALUES, {
    skip:
      !magentoConnectorEnabled ||
      !magentoConnectorId ||
      !!shopifyConnectorId ||
      !inSectionView ||
      !shouldLoadKpiFilterOptions,
    variables: {
      args: {
        metricKeys: ['total_sales'],
        splitsKeys: [],
        filters: [],
        dateRange: { from: dateRange.from, to: dateRange.to },
      },
    },
    fetchPolicy: 'cache-first',
  });

  const { data: inventoryCountryOptionsData } = useQuery(
    GET_INVENTORY_COUNTRY_OPTIONS,
    {
      fetchPolicy: 'cache-first',
    },
  );

  const kpiFilterOptions = useMemo(() => {
    const shopifyOpts =
      kpiShopifyFiltersData?.getShopifyMetricValues?.filtersOptions ?? [];
    const magentoOpts =
      kpiMagentoFiltersData?.getMagentoMetricValues?.filtersOptions ?? [];
    const opts = shopifyOpts.length > 0 ? shopifyOpts : magentoOpts;
    const byKey = (key: string) =>
      opts.find((o: { key: string; values: string[] }) => o.key === key)
        ?.values ?? [];
    return {
      tags: byKey('product_tag'),
      collections: byKey('collection_title'),
      vendors: byKey('vendor'),
      productTypes: byKey('product_type'),
    };
  }, [
    kpiShopifyFiltersData?.getShopifyMetricValues?.filtersOptions,
    kpiMagentoFiltersData?.getMagentoMetricValues?.filtersOptions,
  ]);

  const kpiCountryOptions = useMemo(
    () => inventoryCountryOptionsData?.getInventoryCountryOptions ?? [],
    [inventoryCountryOptionsData],
  );

  const periodSummaryLabel = useMemo(() => {
    if (dateRangePreset === 'custom') {
      return formatRangeLabel(dateRange);
    }
    if (dateRangePreset === 'this_month') {
      return formatRangeLabel(dateRange);
    }
    return '';
  }, [dateRange, dateRangePreset, formatRangeLabel]);

  const compareSummaryLabel = useMemo(() => {
    if (compareMode === 'none' || !compareDateRange) return '';
    return formatRangeLabel(compareDateRange);
  }, [compareDateRange, compareMode, formatRangeLabel]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );

  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const canUseThisMonthPreset = useMemo(() => {
    if (!lookbackMinDate) return true;
    const thisMonthRange = getThisMonthDateRange();
    return thisMonthRange.from >= lookbackMinDate;
  }, [lookbackMinDate]);

  const headerInsightLabel = useMemo(() => {
    const parts: string[] = [];
    if (!isKpiHomeView) {
      parts.push(
        `${selectedMetrics.length} ${selectedMetrics.length === 1 ? 'metric' : 'metrics'} active`,
      );
      if (compareMode !== 'none') {
        parts.push(
          compareMode === 'custom'
            ? 'custom compare enabled'
            : compareMode === 'previous_year'
              ? 'comparing vs previous year'
              : 'comparing vs previous period',
        );
      }
      if (kpiActiveFilterCount > 0) {
        parts.push(
          `${kpiActiveFilterCount} ${kpiActiveFilterCount === 1 ? 'filter' : 'filters'} applied`,
        );
      }
      if (showCharts) {
        parts.push('charts visible');
      }
    } else {
      parts.push('build and save reusable KPI sections');
    }
    return parts.join(' · ');
  }, [
    compareMode,
    isKpiHomeView,
    kpiActiveFilterCount,
    selectedMetrics.length,
    showCharts,
  ]);

  // Query per ottenere le metriche disponibili
  const { data: googleAdsData, loading: googleAdsLoading } = useQuery(
    GET_GOOGLE_ADS_METRICS,
    {
      skip: !googleAdsConnectorId,
    },
  );

  const { data: shopifyData, loading: shopifyLoading } = useQuery(
    GET_SHOPIFY_METRICS,
    {
      skip: !shopifyConnectorId,
    },
  );

  const { data: magentoData, loading: magentoLoading } = useQuery(
    GET_MAGENTO_METRICS,
    {
      skip: !magentoConnectorEnabled || !magentoConnectorId,
    },
  );

  const { data: facebookData, loading: facebookLoading } = useQuery(
    GET_FACEBOOK_METRICS,
    {
      skip: !facebookConnectorId,
    },
  );

  const { data: googleAnalyticsData, loading: googleAnalyticsLoading } =
    useQuery(GET_GOOGLE_ANALYTICS_METRICS, {
      skip: !googleAnalytics4ConnectorId,
    });

  // Query per ottenere le custom formulas
  const { data: customFormulasData, loading: customFormulasLoading } =
    useQuery(GET_CUSTOM_FORMULAS);

  // Query per ottenere le sezioni KPI salvate
  const {
    data: sectionsData,
    loading: sectionsLoading,
    refetch: refetchSections,
  } = useQuery(GET_KPI_SECTIONS, {
    fetchPolicy: 'cache-first',
  });

  const savedSections = useMemo(
    () => (sectionsData?.getKpiSections ?? []) as KpiSection[],
    [sectionsData?.getKpiSections],
  );

  const orderedKpiSections = useMemo(
    () => orderKpiSections(savedSections, sectionOrder),
    [savedSections, sectionOrder],
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        KPI_SECTIONS_ORDER_KEY,
        JSON.stringify(sectionOrder),
      );
    } catch (error) {
      console.error('Error saving KPI sections order to localStorage:', error);
    }
  }, [sectionOrder]);

  useEffect(() => {
    if (savedSections.length === 0) return;

    // Se il server ha già sortOrder nel config, allinea lo stato locale.
    const hasServerOrder = savedSections.some(
      (section) => typeof section.config?.sortOrder === 'number',
    );

    setSectionOrder((prevOrder) => {
      // Durante reorder: mantieni l'ordine ottimistico, aggiorna solo membership.
      const baseOrder = pendingSectionOrderRef.current ?? prevOrder;

      if (hasServerOrder && !pendingSectionOrderRef.current) {
        const serverOrder = orderKpiSections(savedSections).map(
          (section) => section.id,
        );
        if (
          serverOrder.length === baseOrder.length &&
          serverOrder.every((sectionId, index) => sectionId === baseOrder[index])
        ) {
          return prevOrder;
        }
        return serverOrder;
      }

      const savedIds = savedSections.map((section) => section.id);
      const validSavedIds = new Set(savedIds);
      const nextOrder = [
        ...baseOrder.filter((sectionId) => validSavedIds.has(sectionId)),
        ...savedIds.filter((sectionId) => !baseOrder.includes(sectionId)),
      ];

      if (
        nextOrder.length === prevOrder.length &&
        nextOrder.every((sectionId, index) => sectionId === prevOrder[index])
      ) {
        return prevOrder;
      }

      return nextOrder;
    });
  }, [savedSections]);

  const resolvedSectionToUpdate = useMemo(
    () =>
      resolveKpiSectionToUpdate({
        selectedSection,
        lastKnownSectionId,
        sections: savedSections,
      }),
    [selectedSection, lastKnownSectionId, savedSections],
  );

  // Se abbiamo un ultimo sectionId valido ma selectedSection è null, riallinea lo stato
  // (evita che il pulsante rimanga su "Save section" dopo refresh, e rende sicuro l'update).
  useEffect(() => {
    if (!selectedSection && resolvedSectionToUpdate) {
      setSelectedSection(resolvedSectionToUpdate);
    }
  }, [resolvedSectionToUpdate, selectedSection]);

  // Gestione URL per persistenza della sezione selezionata (come Custom Report)
  useEffect(() => {
    // Nota UX: la pagina KPI può non rimontare (es. layout che mantiene le route in memoria).
    // Quindi questa sync deve reagire *sempre* ai cambi dell'URL, non solo al primo mount.
    if (!location.pathname.includes('/kpi')) return;

    const prev = prevLocationRef.current;
    const prevSearchParams = prev ? new URLSearchParams(prev.search) : null;
    const prevHadSection = prevSearchParams?.has('section') ?? false;

    const searchParams = new URLSearchParams(location.search);
    const sectionId = searchParams.get('section');

    // Se NON c'è section nell'URL, mostra la home (griglia sezioni).
    // Questo è il comportamento atteso quando l'utente clicca "KPI" dal menù: sempre griglia,
    // non "restore" dell'ultima sezione aperta.
    if (!sectionId) {
      // IMPORTANT: non resettare durante la creazione di una nuova sezione.
      // Altrimenti, quando rimuoviamo `?section=` per passare da una sezione esistente a "New Section",
      // questa effect azzera lo stato e il selettore metriche "sfarfalla" senza applicare la selezione.
      const shouldForceHomeReset =
        // Primo ingresso su KPI (o arrivo da un'altra pagina)
        !prev?.pathname?.includes('/kpi') ||
        // Passaggio esplicito da una sezione (URL con section) alla home KPI (URL senza section),
        // ma SOLO se non stiamo creando una nuova sezione.
        (prevHadSection && !isCreatingNewSection && !showMetricSelector);

      if (shouldForceHomeReset) {
        setSelectedSection(null);
        setLastKnownSectionId(null);
        setSelectedMetrics([]);
        setIsCreatingNewSection(false);
        setShowMetricSelector(false);
        try {
          localStorage.removeItem(KPI_SECTION_ID_KEY);
        } catch {}
      }

      // Dopo aver sincronizzato lo stato con l'URL, abilita la persistenza dello stato UI.
      if (isInitialMount.current) isInitialMount.current = false;

      prevLocationRef.current = {
        pathname: location.pathname,
        search: location.search,
      };
      return;
    }

    // Se c'è un ID sezione nell'URL, attendi che le sezioni siano caricate e poi carica la sezione.
    if (sectionId && savedSections.length > 0) {
      const section = savedSections.find((s: KpiSection) => s.id === sectionId);
      if (section && selectedSection?.id !== section.id) {
        try {
          const config = section.config;
          if (config.selectedMetrics) {
            setSelectedMetrics(config.selectedMetrics);
          }
          // Carica il preset salvato, altrimenti calcolalo dal dateRange per retrocompatibilità
          if (config.dateRangePreset) {
            setDateRangePreset(config.dateRangePreset);
            setShowCustomDateRange(config.dateRangePreset === 'custom');

            // Se il preset è dinamico, ricalcola sempre in base alla data corrente
            if (config.dateRangePreset === 'this_month') {
              setDateRange(getThisMonthDateRange());
            } else if (config.dateRangePreset !== 'custom') {
              // Per "last N days", ricalcola sempre
              const days = parseInt(config.dateRangePreset, 10);
              if (!isNaN(days)) {
                setDateRange(getLastNDaysDateRange(days));
              } else if (config.dateRange) {
                setDateRange(config.dateRange);
              }
            } else if (config.dateRange) {
              // Per preset "custom", usa le date salvate
              setDateRange(config.dateRange);
            }
          } else if (config.dateRange) {
            // Retrocompatibilità: determina il preset basato sul dateRange se non salvato
            const days = Math.ceil(
              (new Date(config.dateRange.to).getTime() -
                new Date(config.dateRange.from).getTime()) /
                (1000 * 60 * 60 * 24),
            );
            if (days === 7) {
              setDateRangePreset('7');
              setShowCustomDateRange(false);
            } else if (days === 30) {
              setDateRangePreset('30');
              setShowCustomDateRange(false);
            } else if (days === 90) {
              setDateRangePreset('90');
              setShowCustomDateRange(false);
            } else {
              setDateRangePreset('custom');
              setShowCustomDateRange(true);
            }
          }
          if (config.showCharts !== undefined) {
            setShowCharts(config.showCharts);
          }
          if (config.compareMode) {
            setCompareMode(config.compareMode);
            if (config.compareDateRange) {
              setCompareDateRange(config.compareDateRange);
            }
            if (config.compareMode === 'custom') {
              setShowCustomCompareDates(true);
            }
          }
          // Ripristina i filtri Shopify salvati nella section (retro-compat: default vuoti).
          const loadedKpiFilters = parseKpiFiltersConfig(config.kpiFilters);
          setKpiCountryFilter(loadedKpiFilters.countries);
          setKpiCustomerTypeFilter(loadedKpiFilters.customerType);
          setKpiTagFilter(loadedKpiFilters.tags);
          setKpiCollectionFilter(loadedKpiFilters.collections);
          setKpiVendorFilter(loadedKpiFilters.vendors);
          setKpiProductTypeFilter(loadedKpiFilters.productTypes);
          setSelectedSection(section);
          setLastKnownSectionId(section.id);
          // Persistenza: salva l'ultima sezione usata per poter fare "update" anche dopo refresh
          try {
            localStorage.setItem(KPI_SECTION_ID_KEY, section.id);
          } catch {}
          if (isInitialMount.current) isInitialMount.current = false;
          prevLocationRef.current = {
            pathname: location.pathname,
            search: location.search,
          };
          return;
        } catch (error) {
          console.error('Error loading section from URL:', error);
        }
      }

      // Se l'id non corrisponde a nessuna sezione (es. sezione cancellata), pulisci stato e sblocca l'init.
      if (!section) {
        try {
          localStorage.removeItem(KPI_SECTION_ID_KEY);
        } catch {}
        setLastKnownSectionId(null);
        setSelectedSection(null);

        // Se il parametro era nell'URL, rimuovilo per evitare loop/confusione.
        const urlSearchParams = new URLSearchParams(location.search);
        if (urlSearchParams.get('section') === sectionId) {
          urlSearchParams.delete('section');
          navigate(`?${urlSearchParams.toString()}`, { replace: true });
        }

        isInitialMount.current = false;
        prevLocationRef.current = {
          pathname: location.pathname,
          search: location.search,
        };
        return;
      }

      // Se la sezione è già selezionata, riallinea solo gli id e sblocca l'init.
      if (section && selectedSection?.id === section.id) {
        setLastKnownSectionId(section.id);
        if (isInitialMount.current) isInitialMount.current = false;
        prevLocationRef.current = {
          pathname: location.pathname,
          search: location.search,
        };
        return;
      }
    }

    // Fallback: completa l'init
    if (isInitialMount.current) isInitialMount.current = false;
    prevLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [
    location.pathname,
    location.search,
    savedSections,
    selectedSection,
    lastKnownSectionId,
    isCreatingNewSection,
    showMetricSelector,
    selectedMetrics.length,
    navigate,
  ]);

  // Persistenza: quando cambio sezione, aggiorna localStorage (serve per "Save changes" dopo refresh)
  useEffect(() => {
    try {
      if (selectedSection?.id) {
        localStorage.setItem(KPI_SECTION_ID_KEY, selectedSection.id);
        setLastKnownSectionId(selectedSection.id);
      } else {
        localStorage.removeItem(KPI_SECTION_ID_KEY);
        setLastKnownSectionId(null);
      }
    } catch {}
  }, [selectedSection]);

  // Calcola columnOrder prima dell'useEffect che lo usa
  const columnOrder = useMemo(
    () => selectedMetrics.map((m) => m.id),
    [selectedMetrics],
  );

  // Salva lo stato in localStorage ogni volta che cambia (debounced)
  useEffect(() => {
    if (!isInitialMount.current) {
      const timeoutId = setTimeout(() => {
        saveStateToStorage({
          selectedMetrics,
          dateRange,
          dateRangePreset,
          showCharts,
          columnOrder,
        });
      }, 500); // Debounce di 500ms per evitare troppi salvataggi

      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedMetrics,
    dateRange,
    dateRangePreset,
    showCharts,
    columnOrder,
    saveStateToStorage,
  ]);

  // Mutations per gestire le sezioni
  const [createKpiSection] = useMutation(CREATE_KPI_SECTION);
  const [updateKpiSection] = useMutation(UPDATE_KPI_SECTION);
  const [reorderKpiSections] = useMutation(REORDER_KPI_SECTIONS);
  const [deleteKpiSection] = useMutation(DELETE_KPI_SECTION, {
    refetchQueries: ['getKpiSections'],
  });

  // Salvataggio rapido senza dialog (quando c'è già una sezione selezionata)
  const handleQuickSave = useCallback(
    async (
      metricsToSave?: SelectedMetric[],
      sectionOverride?: KpiSection | null,
      options?: KpiQuickSaveOptions,
    ) => {
      const section = sectionOverride ?? resolvedSectionToUpdate;
      const silent = Boolean(options?.silent);

      if (!section) {
        // Non aprire dialog automaticamente da autosave: il create deve essere intenzionale via click.
        return;
      }

      try {
        if (!silent) setIsSavingSection(true);
        // Anti-race: se l'utente aggiunge metriche molto velocemente, accetta solo l'ultima risposta.
        const seq = ++saveSeqRef.current;

        const metrics = metricsToSave || selectedMetrics;
        const config = {
          selectedMetrics: metrics,
          dateRange,
          dateRangePreset,
          showCharts,
          columnOrder: metrics.map((m) => m.id),
          compareMode,
          compareDateRange,
          // Mantieni l'ordine card se già persistito nel JSON config.
          ...(typeof section.config?.sortOrder === 'number'
            ? { sortOrder: section.config.sortOrder }
            : {}),
          // Persisti i filtri Shopify (Country/CustomerType/etc.) nella sezione.
          // Senza questo i filtri non vengono ripristinati quando ricarichi la section.
          kpiFilters: {
            countries: kpiCountryFilter,
            customerType: kpiCustomerTypeFilter,
            tags: kpiTagFilter,
            collections: kpiCollectionFilter,
            vendors: kpiVendorFilter,
            productTypes: kpiProductTypeFilter,
          },
        };

        const { data } = await updateKpiSection({
          variables: {
            id: section.id,
            input: {
              name: section.name, // Mantieni il nome esistente
              config,
            },
          },
        });

        if (seq !== saveSeqRef.current) return; // risposta vecchia, ignora

        if (data?.updateKpiSection) {
          setSelectedSection(data.updateKpiSection);
          setLastKnownSectionId(data.updateKpiSection.id);
          try {
            localStorage.setItem(KPI_SECTION_ID_KEY, data.updateKpiSection.id);
          } catch {}
          if (!silent) snackbar('Section updated', 'success');
          refetchSections();
        }
      } catch (error: any) {
        console.error('Error auto-saving section:', error);
        const errorMessage =
          error?.message ||
          error?.graphQLErrors?.[0]?.message ||
          'Unknown error';
        snackbar(`Error saving section: ${errorMessage}`, 'error');
      } finally {
        if (!silent) setIsSavingSection(false);
      }
    },
    [
      resolvedSectionToUpdate,
      selectedMetrics,
      dateRange,
      dateRangePreset,
      showCharts,
      kpiCountryFilter,
      kpiCustomerTypeFilter,
      kpiTagFilter,
      kpiCollectionFilter,
      kpiVendorFilter,
      kpiProductTypeFilter,
      compareMode,
      compareDateRange,
      updateKpiSection,
      refetchSections,
      snackbar,
    ],
  );

  // Nota: la maggior parte delle modifiche alla sezione KPI resta "manuale" (pulsante "Update section").
  // Eccezione: i target KPI vengono auto-persistiti perché devono sopravvivere a refresh/ricarichi dati.

  // Query per ottenere i valori delle metriche selezionate
  const selectedMetricKeys = useMemo(() => {
    return selectedMetrics.map((sm) => sm.metric.key);
  }, [selectedMetrics]);

  const selectedConnectorTypes = useMemo(() => {
    return Array.from(new Set(selectedMetrics.map((sm) => sm.connectorType)));
  }, [selectedMetrics]);

  // Gestione drag and drop in stile Custom Report (useDragAndDrop)
  const {
    dragState,
    handleDragStart,
    handleDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    getColumnClassName,
  } = useDragAndDrop({
    columnOrder,
    onColumnReorder: (newOrder) => {
      const map = new Map(selectedMetrics.map((m) => [m.id, m]));
      const reordered = newOrder
        .map((id) => map.get(id))
        .filter((m): m is SelectedMetric => Boolean(m));
      setSelectedMetrics(reordered);
    },
    enableSound: false,
    enableVibration: true,
  });

  const handleSectionDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>, sectionId: string) => {
      event.dataTransfer.setData('text/plain', sectionId);
      event.dataTransfer.effectAllowed = 'move';
      sectionDragClickGuardRef.current = false;
      draggedSectionIdRef.current = sectionId;
      setDraggedSectionId(sectionId);
    },
    [],
  );

  const handleSectionDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>, sectionId: string) => {
      const currentDraggedSectionId = draggedSectionIdRef.current;
      if (!currentDraggedSectionId || currentDraggedSectionId === sectionId) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setSectionDropTargetId(sectionId);
    },
    [],
  );

  const handleSectionDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>, targetSectionId: string) => {
      event.preventDefault();

      const sourceSectionId =
        event.dataTransfer.getData('text/plain') || draggedSectionIdRef.current;
      if (!sourceSectionId || sourceSectionId === targetSectionId) {
        return;
      }

      const currentOrder = orderedKpiSections.map((section) => section.id);
      const sourceIndex = currentOrder.indexOf(sourceSectionId);
      const targetIndex = currentOrder.indexOf(targetSectionId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return;
      }

      const nextOrder = [...currentOrder];
      const [movedSectionId] = nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, movedSectionId);

      sectionDragClickGuardRef.current = true;
      draggedSectionIdRef.current = null;
      pendingSectionOrderRef.current = nextOrder;
      setSectionOrder(nextOrder);
      setDraggedSectionId(null);
      setSectionDropTargetId(null);

      // Persisti l'ordine nel JSON config lato server (niente migrazione DB).
      try {
        await reorderKpiSections({
          variables: { orderedIds: nextOrder },
        });
        await refetchSections();
      } catch (error) {
        console.error('Error persisting KPI sections order:', error);
        snackbar('Could not save section order', 'error');
      } finally {
        pendingSectionOrderRef.current = null;
      }
    },
    [orderedKpiSections, reorderKpiSections, refetchSections, snackbar],
  );

  const handleSectionDragEnd = useCallback(() => {
    if (draggedSectionIdRef.current) {
      sectionDragClickGuardRef.current = true;
      window.setTimeout(() => {
        sectionDragClickGuardRef.current = false;
      }, 150);
    }

    draggedSectionIdRef.current = null;
    setDraggedSectionId(null);
    setSectionDropTargetId(null);
  }, []);

  // Aggiungi metrica
  const handleAddMetric = useCallback(
    (metric: Metric, connectorType: ConnectorType | string) => {
      // Se siamo nella home view e non ci sono sezioni salvate, crea automaticamente una nuova sezione
      const isHomeView =
        !selectedSection &&
        !lastKnownSectionId &&
        !isCreatingNewSection &&
        selectedMetrics.length === 0;
      const hasNoSections = savedSections.length === 0;

      if (isHomeView && hasNoSections) {
        // Crea una nuova sezione automaticamente
        setSelectedMetrics([]);
        setDateRange(getLastNDaysDateRange(30));
        setDateRangePreset('30');
        setShowCharts(true);
        setCompareMode('none');
        setCompareDateRange(null);
        setShowCustomCompareDates(false);
        setSelectedSection(null);
        setIsCreatingNewSection(true);
        try {
          localStorage.removeItem(KPI_SECTION_ID_KEY);
        } catch {}
        setLastKnownSectionId(null);

        // Rimuovi section ID dall'URL
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('section');
        navigate(`?${searchParams.toString()}`, { replace: true });
      }

      const newMetric: SelectedMetric = {
        id: `${connectorType}-${metric.key}-${Date.now()}`,
        metric,
        connectorType,
        showChart: true,
        target: null,
      };
      const updatedMetrics = [...selectedMetrics, newMetric];
      setSelectedMetrics(updatedMetrics);
      setShowMetricSelector(false);
      // Salvataggio sezione manuale: l'utente conferma con "Update section".
    },
    [
      selectedMetrics,
      selectedSection,
      lastKnownSectionId,
      isCreatingNewSection,
      savedSections.length,
      location.search,
      navigate,
    ],
  );

  // Rimuovi metrica
  const handleRemoveMetric = useCallback(
    (id: string) => {
      const updatedMetrics = selectedMetrics.filter((m) => m.id !== id);
      setSelectedMetrics(updatedMetrics);
      // Salvataggio sezione manuale: l'utente conferma con "Update section".
    },
    [selectedMetrics],
  );

  // Toggle grafico per metrica
  const handleToggleChart = useCallback(
    (id: string) => {
      const updatedMetrics = selectedMetrics.map((m) =>
        m.id === id ? { ...m, showChart: !m.showChart } : m,
      );
      setSelectedMetrics(updatedMetrics);
      // Salvataggio sezione manuale: l'utente conferma con "Update section".
    },
    [selectedMetrics],
  );

  const handleSaveMetricTarget = useCallback(
    (id: string, target: KpiMetricTarget) => {
      setSelectedMetrics((prevMetrics) => {
        const nextMetrics = prevMetrics.map((metric) =>
          metric.id === id ? { ...metric, target } : metric,
        );

        // Persistenza server: `target` vive dentro `selectedMetrics` nel JSON della sezione.
        // Senza questo, dopo refresh/ricarico dati il target sembra "sparire" anche se l'UI lo mostra.
        void handleQuickSave(nextMetrics, undefined, { silent: true });

        return nextMetrics;
      });
    },
    [handleQuickSave],
  );

  const handleDeleteMetricTarget = useCallback(
    (id: string) => {
      setSelectedMetrics((prevMetrics) => {
        const nextMetrics = prevMetrics.map((metric) =>
          metric.id === id ? { ...metric, target: null } : metric,
        );

        void handleQuickSave(nextMetrics, undefined, { silent: true });

        return nextMetrics;
      });
    },
    [handleQuickSave],
  );

  // Gestione date range
  const handleDateRangeChange = useCallback(
    (preset: string) => {
      if (preset !== 'custom' && preset !== 'this_month') {
        const days = parseInt(preset, 10);
        if (!isNaN(days) && !canUsePresetDays(days)) {
          snackbar(getLookbackPresetBlockedMessage(lookbackDays), 'warning');
          return;
        }
      }
      if (preset === 'this_month' && !canUseThisMonthPreset) {
        snackbar(getLookbackThisMonthBlockedMessage(lookbackDays), 'warning');
        return;
      }

      setDateRangePreset(preset);
      setShowCustomDateRange(preset === 'custom');
      setTempCustomDates(null);

      if (preset === 'this_month') {
        const newDateRange = getThisMonthDateRange();
        setDateRange(newDateRange);

        // Aggiorna anche il periodo di comparazione se attivo
        if (compareMode === 'previous_period') {
          setCompareDateRange(getPreviousPeriodDateRange(newDateRange));
        } else if (compareMode === 'previous_year') {
          setCompareDateRange(getPreviousYearDateRange(newDateRange));
        }
      } else if (preset !== 'custom') {
        const days = parseInt(preset, 10);
        const newDateRange = getLastNDaysDateRange(days);
        setDateRange(newDateRange);

        // Aggiorna anche il periodo di comparazione se attivo
        if (compareMode === 'previous_period') {
          setCompareDateRange(getPreviousPeriodDateRange(newDateRange));
        } else if (compareMode === 'previous_year') {
          setCompareDateRange(getPreviousYearDateRange(newDateRange));
        }
      } else {
        setTempCustomDates({
          from: dateRange.from,
          to: dateRange.to,
        });
      }
    },
    [
      dateRange,
      compareMode,
      canUsePresetDays,
      canUseThisMonthPreset,
      snackbar,
      lookbackDays,
    ],
  );

  // Gestione compare mode
  const handleCompareModeChange = useCallback(
    (mode: string) => {
      if (mode === 'previous_period' && !canUsePreviousPeriodCompare) {
        snackbar(
          getLookbackCompareBlockedMessage('previous_period', lookbackDays),
          'warning',
        );
        return;
      }
      if (mode === 'previous_year' && !canUsePreviousYearCompare) {
        snackbar(
          getLookbackCompareBlockedMessage('previous_year', lookbackDays),
          'warning',
        );
        return;
      }

      setCompareMode(mode);

      if (mode === 'none') {
        setCompareDateRange(null);
        setShowCustomCompareDates(false);
        setTempCustomCompareDates(null);
      } else if (mode === 'previous_period') {
        setCompareDateRange(getPreviousPeriodDateRange(dateRange));
        setShowCustomCompareDates(false);
      } else if (mode === 'previous_year') {
        setCompareDateRange(getPreviousYearDateRange(dateRange));
        setShowCustomCompareDates(false);
      } else if (mode === 'custom') {
        setShowCustomCompareDates(true);
        setTempCustomCompareDates(
          compareDateRange || {
            from: dateRange.from,
            to: dateRange.to,
          },
        );
      }
    },
    [
      dateRange,
      compareDateRange,
      canUsePreviousPeriodCompare,
      canUsePreviousYearCompare,
      snackbar,
      lookbackDays,
    ],
  );

  // Aggiorna compare date range quando cambia il date range principale
  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
  }, [dateRange, compareMode]);

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  const handleApplyCustomCompareDates = useCallback(() => {
    if (tempCustomCompareDates?.from && tempCustomCompareDates?.to) {
      if (tempCustomCompareDates.from <= tempCustomCompareDates.to) {
        if (!isRangeWithinLookback(tempCustomCompareDates)) {
          snackbar(
            getLookbackCustomRangeBlockedMessage(lookbackDays, 'compare'),
            'warning',
          );
          return;
        }
        setCompareDateRange(tempCustomCompareDates);
        setShowCustomCompareDates(false);
        setTempCustomCompareDates(null);
      }
    }
  }, [tempCustomCompareDates, isRangeWithinLookback, snackbar, lookbackDays]);

  const handleApplyCustomDates = useCallback(() => {
    if (tempCustomDates?.from && tempCustomDates?.to) {
      if (tempCustomDates.from <= tempCustomDates.to) {
        if (!isRangeWithinLookback(tempCustomDates)) {
          snackbar(
            getLookbackCustomRangeBlockedMessage(lookbackDays, 'date'),
            'warning',
          );
          return;
        }
        setDateRange(tempCustomDates);
        setShowCustomDateRange(false);
        setTempCustomDates(null);
      }
    }
  }, [tempCustomDates, isRangeWithinLookback, snackbar, lookbackDays]);

  // Carica una sezione salvata
  const handleLoadSection = useCallback(
    (section: KpiSection) => {
      try {
        const config = section.config;
        if (config.selectedMetrics) {
          setSelectedMetrics(config.selectedMetrics);
        }
        // Carica il preset salvato, altrimenti calcolalo dal dateRange per retrocompatibilità
        if (config.dateRangePreset) {
          setDateRangePreset(config.dateRangePreset);
          setShowCustomDateRange(config.dateRangePreset === 'custom');

          // Se il preset è dinamico, ricalcola sempre in base alla data corrente
          if (config.dateRangePreset === 'this_month') {
            setDateRange(getThisMonthDateRange());
          } else if (config.dateRangePreset !== 'custom') {
            // Per "last N days", ricalcola sempre
            const days = parseInt(config.dateRangePreset, 10);
            if (!isNaN(days)) {
              setDateRange(getLastNDaysDateRange(days));
            } else if (config.dateRange) {
              setDateRange(config.dateRange);
            }
          } else if (config.dateRange) {
            // Per preset "custom", usa le date salvate
            setDateRange(config.dateRange);
          }
        } else if (config.dateRange) {
          // Retrocompatibilità: determina il preset basato sul dateRange se non salvato
          const days = Math.ceil(
            (new Date(config.dateRange.to).getTime() -
              new Date(config.dateRange.from).getTime()) /
              (1000 * 60 * 60 * 24),
          );
          if (days === 7) {
            setDateRangePreset('7');
            setShowCustomDateRange(false);
          } else if (days === 30) {
            setDateRangePreset('30');
            setShowCustomDateRange(false);
          } else if (days === 90) {
            setDateRangePreset('90');
            setShowCustomDateRange(false);
          } else {
            setDateRangePreset('custom');
            setShowCustomDateRange(true);
          }
        }
        if (config.showCharts !== undefined) {
          setShowCharts(config.showCharts);
        }
        if (config.columnOrder) {
          // Il columnOrder viene gestito automaticamente da useDragAndDrop
        }
        if (config.compareMode) {
          setCompareMode(config.compareMode);
          if (config.compareDateRange) {
            setCompareDateRange(config.compareDateRange);
            if (config.compareMode === 'custom') {
              setShowCustomCompareDates(true);
            }
          }
        }
        // Ripristina i filtri Shopify salvati nella section (retro-compat: default vuoti).
        const loadedKpiFilters = parseKpiFiltersConfig(config.kpiFilters);
        setKpiCountryFilter(loadedKpiFilters.countries);
        setKpiCustomerTypeFilter(loadedKpiFilters.customerType);
        setKpiTagFilter(loadedKpiFilters.tags);
        setKpiCollectionFilter(loadedKpiFilters.collections);
        setKpiVendorFilter(loadedKpiFilters.vendors);
        setKpiProductTypeFilter(loadedKpiFilters.productTypes);
        setSelectedSection(section);
        setLastKnownSectionId(section.id);
        setSectionsDrawerOpen(false);
        setIsCreatingNewSection(false); // Reset flag quando si carica una sezione esistente

        // Aggiorna URL per mantenere la sezione selezionata dopo refresh
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('section', section.id);
        navigate(`?${searchParams.toString()}`, { replace: true });

        // Persistenza: salva anche in localStorage come fallback (se l'URL viene perso)
        try {
          localStorage.setItem(KPI_SECTION_ID_KEY, section.id);
        } catch {}

        snackbar(`Section "${section.name}" loaded successfully`, 'success');
      } catch (error) {
        console.error('Error loading section:', error);
        snackbar('Error loading section', 'error');
      }
    },
    [snackbar, location.search, navigate],
  );

  const handleSectionCardClick = useCallback(
    (section: KpiSection) => {
      if (sectionDragClickGuardRef.current) {
        sectionDragClickGuardRef.current = false;
        return;
      }

      handleLoadSection(section);
    },
    [handleLoadSection],
  );

  // Crea una nuova sezione vuota
  const handleNewSection = useCallback(() => {
    setSelectedMetrics([]);
    setDateRange(getLastNDaysDateRange(30));
    setDateRangePreset('30');
    setShowCharts(true);
    setCompareMode('none');
    setCompareDateRange(null);
    setShowCustomCompareDates(false);
    setSelectedSection(null);
    setSectionsDrawerOpen(false);
    setIsCreatingNewSection(true); // Flag per mostrare le KPI cards durante la creazione
    try {
      localStorage.removeItem(KPI_SECTION_ID_KEY);
    } catch {}
    setLastKnownSectionId(null);

    // Rimuovi section ID dall'URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('section');
    navigate(`?${searchParams.toString()}`, { replace: true });

    // UX: chi crea una nuova sezione vuole subito scegliere le metriche.
    // Apriamo direttamente il selector per evitare una "home" che sembra immobile.
    setShowMetricSelector(true);

    snackbar('New section created. Start adding metrics!', 'success');
  }, [snackbar, location.search, navigate]);

  // Genera report AI basato sui KPI selezionati (o tutte le metriche disponibili se nessuna selezionata)
  const handleGenerateAIReport = useCallback(async () => {
    setIsGeneratingReport(true);
    try {
      // Se non ci sono metriche selezionate, passa array vuoto e il backend userà tutte le metriche disponibili
      const metricsToSend =
        selectedMetrics.length > 0
          ? selectedMetrics.map((m) => ({
              id: m.id,
              metric: m.metric,
              connectorType: m.connectorType,
              showChart: m.showChart,
            }))
          : []; // Array vuoto = usa tutte le metriche disponibili automaticamente

      // Chiamata JSON preflight per ottenere HTML
      const preflight = await generateAIReportData({
        variables: {
          input: {
            selectedMetrics: metricsToSend,
            dateRange: {
              from: dateRange.from,
              to: dateRange.to,
            },
            compareDateRange: compareDateRange
              ? {
                  from: compareDateRange.from,
                  to: compareDateRange.to,
                }
              : null,
            compareMode: compareMode !== 'none' ? compareMode : null,
          },
        },
      });

      const payload = preflight.data?.generateKpiAIReportData;
      if (typeof payload === 'string') {
        setAiReport(payload);
      } else if (payload && payload.html) {
        setAiReport(payload.html);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error generating AI report (preflight):', error);
      // Fallback alla vecchia mutation
      try {
        // Se non ci sono metriche selezionate, passa array vuoto e il backend userà tutte le metriche disponibili
        const metricsToSend =
          selectedMetrics.length > 0
            ? selectedMetrics.map((m) => ({
                id: m.id,
                metric: m.metric,
                connectorType: m.connectorType,
                showChart: m.showChart,
              }))
            : []; // Array vuoto = usa tutte le metriche disponibili automaticamente

        const result = await generateAIReport({
          variables: {
            input: {
              selectedMetrics: metricsToSend,
              dateRange: {
                from: dateRange.from,
                to: dateRange.to,
              },
              compareDateRange: compareDateRange
                ? {
                    from: compareDateRange.from,
                    to: compareDateRange.to,
                  }
                : null,
              compareMode: compareMode !== 'none' ? compareMode : null,
            },
          },
        });
        setAiReport(result.data?.generateKpiAIReport || null);
      } catch (err2: any) {
        console.error('Error generating AI report (fallback):', err2);
        snackbar(
          err2?.message ||
            'Error generating AI report. Please try again later.',
          'error',
        );
      }
    } finally {
      setIsGeneratingReport(false);
      // Aggiorna il trial status per riflettere le chiamate AI scalate
      try {
        await refetchTrialStatus();
      } catch (error) {
        console.error('Error refetching trial status:', error);
      }
    }
  }, [
    selectedMetrics,
    dateRange,
    compareDateRange,
    compareMode,
    generateAIReport,
    generateAIReportData,
    snackbar,
    refetchTrialStatus,
  ]);

  // Funzione per aspettare che le immagini si carichino
  const waitForImages = useCallback((element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll('img');
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          checkComplete();
        } else {
          img.onload = checkComplete;
          img.onerror = checkComplete;
        }
      });
    });
  }, []);

  // Funzione per download PDF
  const downloadAsPDF = useCallback(async () => {
    try {
      // Carica html2pdf.js da CDN
      if (!(window as any).html2pdf) {
        const script = document.createElement('script');
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = () => {
          setTimeout(() => downloadAsPDF(), 100);
        };
        document.head.appendChild(script);
        return;
      }

      const element = document.querySelector('.kipify-report') as HTMLElement;
      if (!element) {
        console.error('Report element not found');
        return;
      }

      // Nascondi controlli non utili nel PDF
      const headerActions = document.querySelector(
        '.header-actions',
      ) as HTMLElement | null;
      const originalHeaderDisplay = headerActions?.style.display;
      if (headerActions) headerActions.style.display = 'none';

      // Aspetta che le immagini si carichino
      await waitForImages(element);

      const opt = {
        margin: 0.5,
        filename: 'kipify-kpi-report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          scrollX: 0,
          scrollY: 0,
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      };

      await (window as any).html2pdf().set(opt).from(element).save();

      // Ripristina header
      if (headerActions && originalHeaderDisplay !== undefined) {
        headerActions.style.display = originalHeaderDisplay;
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      snackbar('Error downloading PDF', 'error');
    }
  }, [waitForImages, snackbar]);

  // Salva la sezione corrente (con dialog per nome)
  const handleSaveSection = useCallback(async () => {
    if (!sectionName.trim()) {
      snackbar('Please enter a section name', 'error');
      return;
    }

    try {
      setIsSavingSection(true);
      const existingSortOrder =
        typeof selectedSection?.config?.sortOrder === 'number'
          ? selectedSection.config.sortOrder
          : nextKpiSectionSortOrder(savedSections);
      const config = {
        selectedMetrics,
        dateRange,
        dateRangePreset,
        showCharts,
        columnOrder,
        compareMode,
        compareDateRange,
        sortOrder: existingSortOrder,
        // Persisti i filtri Shopify nella sezione salvata/aggiornata.
        kpiFilters: {
          countries: kpiCountryFilter,
          customerType: kpiCustomerTypeFilter,
          tags: kpiTagFilter,
          collections: kpiCollectionFilter,
          vendors: kpiVendorFilter,
          productTypes: kpiProductTypeFilter,
        },
      };

      if (selectedSection) {
        // Update
        const { data } = await updateKpiSection({
          variables: {
            id: selectedSection.id,
            input: {
              name: sectionName.trim(),
              config,
            },
          },
        });
        if (data?.updateKpiSection) {
          // Mantieni selectedSection impostato sulla sezione aggiornata
          setSelectedSection(data.updateKpiSection);
          setIsCreatingNewSection(false); // Reset flag dopo il salvataggio
          snackbar('Section updated successfully', 'success');
          try {
            localStorage.setItem(KPI_SECTION_ID_KEY, data.updateKpiSection.id);
          } catch {}
        }
      } else {
        // Create
        const { data } = await createKpiSection({
          variables: {
            input: {
              name: sectionName.trim(),
              config,
            },
          },
        });
        if (data?.createKpiSection) {
          // Imposta selectedSection sulla nuova sezione creata
          setSelectedSection(data.createKpiSection);
          setIsCreatingNewSection(false); // Reset flag dopo la creazione
          // Aggiorna URL per mantenere la sezione selezionata dopo refresh
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('section', data.createKpiSection.id);
          navigate(`?${searchParams.toString()}`, { replace: true });
          try {
            localStorage.setItem(KPI_SECTION_ID_KEY, data.createKpiSection.id);
          } catch {}
          snackbar('Section saved successfully', 'success');
        }
      }

      setSaveSectionDialogOpen(false);
      setSectionName('');
      refetchSections();
    } catch (error: any) {
      console.error('Error saving section:', error);
      const errorMessage =
        error?.message || error?.graphQLErrors?.[0]?.message || 'Unknown error';
      snackbar(`Error saving section: ${errorMessage}`, 'error');
    } finally {
      setIsSavingSection(false);
    }
  }, [
    sectionName,
    selectedMetrics,
    dateRange,
    dateRangePreset,
    showCharts,
    columnOrder,
    kpiCountryFilter,
    kpiCustomerTypeFilter,
    kpiTagFilter,
    kpiCollectionFilter,
    kpiVendorFilter,
    kpiProductTypeFilter,
    compareMode,
    compareDateRange,
    selectedSection,
    savedSections,
    createKpiSection,
    updateKpiSection,
    refetchSections,
    snackbar,
    location.search,
    navigate,
  ]);

  // Elimina una sezione
  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!window.confirm('Are you sure you want to delete this section?')) {
        return;
      }

      try {
        await deleteKpiSection({
          variables: { id: sectionId },
        });
        snackbar('Section deleted successfully', 'success');
        if (selectedSection?.id === sectionId) {
          setSelectedSection(null);
        }
      } catch (error: any) {
        console.error('Error deleting section:', error);
        const errorMessage =
          error?.message ||
          error?.graphQLErrors?.[0]?.message ||
          'Unknown error';
        snackbar(`Error deleting section: ${errorMessage}`, 'error');
      }
    },
    [deleteKpiSection, selectedSection, snackbar],
  );

  const handleOpenRenameSection = useCallback((section: KpiSection) => {
    setSectionPendingRename(section);
    setRenameNameDraft(section.name);
    setRenameSectionDialogOpen(true);
  }, []);

  const handleConfirmRenameSection = useCallback(async () => {
    if (!sectionPendingRename) return;
    const name = renameNameDraft.trim();
    if (!name) {
      snackbar('Please enter a section name', 'error');
      return;
    }
    setIsRenamingSection(true);
    try {
      const { data } = await updateKpiSection({
        variables: {
          id: sectionPendingRename.id,
          input: {
            name,
            config: sectionPendingRename.config,
          },
        },
      });
      if (data?.updateKpiSection) {
        snackbar('Section renamed', 'success');
        if (selectedSection?.id === sectionPendingRename.id) {
          setSelectedSection(data.updateKpiSection);
        }
        void refetchSections();
        setRenameSectionDialogOpen(false);
        setSectionPendingRename(null);
        setRenameNameDraft('');
      }
    } catch (error: any) {
      console.error('Error renaming section:', error);
      const errorMessage =
        error?.message || error?.graphQLErrors?.[0]?.message || 'Unknown error';
      snackbar(`Error renaming section: ${errorMessage}`, 'error');
    } finally {
      setIsRenamingSection(false);
    }
  }, [
    sectionPendingRename,
    renameNameDraft,
    updateKpiSection,
    selectedSection,
    snackbar,
    refetchSections,
  ]);

  // Raggruppa metriche per connettore - Kipify per prime
  const availableMetrics = useMemo(() => {
    const metrics: Array<{
      metric: Metric;
      connectorType: ConnectorType | string;
    }> = [];

    // Kipify - metriche dal hook useConnectors o hardcoded come fallback (PRIME)
    const kipifyMetricsList =
      kipifyMetrics && Array.isArray(kipifyMetrics) && kipifyMetrics.length > 0
        ? kipifyMetrics
        : ([
            {
              id: 'kipify_total_marketing_spend',
              key: 'total_marketing_spend',
              label: 'Total Marketing Spend',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'currency' as const,
            },
            {
              id: 'kipify_markup',
              key: 'markup',
              label: 'Markup',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'number' as const,
            },
            {
              id: 'kipify_roas',
              key: 'roas',
              label: 'Roas',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'number' as const,
            },
            {
              id: 'kipify_acos',
              key: 'acos',
              label: 'ACOS',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'percentage' as const,
            },
            {
              id: 'kipify_acos_total',
              key: 'acos_total',
              label: 'ACOS (shipping incl.)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'percentage' as const,
            },
            {
              id: 'kipify_oac',
              key: 'oac',
              label: 'OAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'currency' as const,
            },
            {
              id: 'kipify_cac',
              key: 'cac',
              label: 'CAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'currency' as const,
            },
            {
              id: 'kipify_cac_new_customers',
              key: 'cac_new_customers',
              label: 'CAC (New Customers)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: 'currency' as const,
            },
          ] as Metric[]);

    kipifyMetricsList.forEach((m: Metric) => {
      metrics.push({ metric: m, connectorType: MetricsByConnectorType.KIPIFY });
    });

    // Google Ads - struttura: getGoogleAdsMetricsList.metrics[]
    // Filtra solo le metriche (esclude splits)
    if (googleAdsData?.getGoogleAdsMetricsList?.metrics) {
      googleAdsData.getGoogleAdsMetricsList.metrics
        .filter((m: Metric) => m.category === 'metric')
        .forEach((m: Metric) => {
          metrics.push({ metric: m, connectorType: ConnectorType.GOOGLE_ADS });
        });
    }

    // Shopify - struttura: getShopifyMetrics.metrics[]
    // Filtra "Customers" come nel codice esistente
    if (shopifyData?.getShopifyMetrics?.metrics) {
      shopifyData.getShopifyMetrics.metrics
        .filter((m: Metric) => m.label !== 'Customers')
        .forEach((m: Metric) => {
          metrics.push({ metric: m, connectorType: ConnectorType.SHOPIFY });
        });
    }

    if (magentoData?.getMagentoMetrics?.metrics) {
      magentoData.getMagentoMetrics.metrics.forEach((m: Metric) => {
        metrics.push({ metric: m, connectorType: ConnectorType.MAGENTO });
      });
    }

    // Facebook - struttura: getFacebookMetrics.metrics[]
    if (facebookData?.getFacebookMetrics?.metrics) {
      facebookData.getFacebookMetrics.metrics.forEach((m: Metric) => {
        metrics.push({ metric: m, connectorType: ConnectorType.FACEBOOK });
      });
    }

    // Google Analytics - struttura: getGoogleAnalyticsMetrics[].metrics[]
    if (googleAnalyticsData?.getGoogleAnalyticsMetrics) {
      const gaData = Array.isArray(
        googleAnalyticsData.getGoogleAnalyticsMetrics,
      )
        ? googleAnalyticsData.getGoogleAnalyticsMetrics
        : [googleAnalyticsData.getGoogleAnalyticsMetrics];

      gaData.forEach((group: any) => {
        if (group.metrics && Array.isArray(group.metrics)) {
          group.metrics.forEach((m: Metric) => {
            metrics.push({
              metric: m,
              connectorType: ConnectorType.GOOGlE_ANALYTICS4,
            });
          });
        }
      });
    }

    if (
      databaseConnectorId &&
      Array.isArray(databaseMetrics) &&
      databaseMetrics.length > 0
    ) {
      databaseMetrics.forEach((m: Metric) => {
        metrics.push({ metric: m, connectorType: ConnectorType.DATABASE });
      });
    }

    // Custom Formulas - aggiunte in modo isolato dopo Kipify
    // Funzione di mapping tra MetricFormatType e ValueType
    const mapFormatTypeToValueType = (formatType: string): ValueType => {
      // Il backend usa enum tipo "currency/percent/number/duration" (lowercase) ma teniamo fallback robusto.
      const t = String(formatType || '').toLowerCase();
      switch (t) {
        case 'currency':
          return ValueType.currency;
        case 'percent':
        case 'percentage':
          return ValueType.percentage;
        case 'duration':
          return ValueType.duration;
        case 'number':
        default:
          return ValueType.number;
      }
    };

    if (customFormulasData?.getAllCustomFormulas) {
      customFormulasData.getAllCustomFormulas.forEach((formula: any) => {
        metrics.push({
          metric: {
            id: formula.id,
            key: formula.id,
            label: formula.name,
            category: 'metric',
            type: MetricsByConnectorType.CUSTOM_FORMULA,
            valueType: mapFormatTypeToValueType(
              formula.format?.type || 'NUMBER',
            ),
          },
          connectorType: MetricsByConnectorType.CUSTOM_FORMULA,
        });
      });
    }

    return metrics;
  }, [
    googleAdsData,
    shopifyData,
    magentoData,
    facebookData,
    googleAnalyticsData,
    kipifyMetrics,
    databaseConnectorId,
    databaseMetrics,
    customFormulasData,
  ]);

  return (
    <Container>
      <Box
        sx={{
          height: 4,
          borderRadius: '0 0 4px 4px',
          background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
          mb: 2,
          mx: -0.5,
        }}
      />

      <HeaderSection
        sx={{
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: palette.grey[200],
          background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
          mt: -0.5,
          pt: 2,
          px: 2,
          mx: -2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
            opacity: 0.6,
          },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            bgcolor: `${palette.primary.main}18`,
            border: '1px solid',
            borderColor: `${palette.primary.main}35`,
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: palette.primary.main,
              flexShrink: 0,
              boxShadow: `0 0 0 2px ${palette.primary.main}40`,
            }}
          />
          <Typography
            sx={{
              color: palette.primary.main,
              fontWeight: 700,
              letterSpacing: 1.1,
              fontSize: '0.7rem',
            }}
          >
            KPI DASHBOARD
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Typography
            variant="h4"
            sx={{
              color: palette.grey[800],
              fontWeight: 800,
              letterSpacing: '-0.025em',
              lineHeight: 1.2,
            }}
          >
            Performance signals, trends and comparisons
          </Typography>
          <Box
            sx={{
              width: '58%',
              height: 3,
              borderRadius: '0 0 2px 2px',
              background: `linear-gradient(90deg, ${palette.primary.main}, transparent)`,
              opacity: 0.5,
              mt: 0.5,
            }}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, mb: 1.5, maxWidth: 620 }}
        >
          Monitor your key performance indicators with customizable metrics,
          clean period comparisons and saved KPI sections.
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, ${palette.primary.main}08 100%)`,
            border: '1px solid',
            borderColor: `${palette.primary.main}25`,
            borderLeft: '5px solid',
            borderLeftColor: palette.primary.main,
            boxShadow:
              '0 4px 20px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
            position: 'relative',
            maxWidth: 560,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: palette.grey[600],
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            Key insight
          </Typography>
          <Typography
            sx={{
              fontSize: '1.2rem',
              fontWeight: 800,
              color: palette.primary.main,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {isKpiHomeView
              ? 'Create KPI workspaces that are easy to reuse'
              : 'Your KPI view is live and configurable'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[800],
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.45,
            }}
          >
            {headerInsightLabel}
          </Typography>
        </Box>

        {isKpiHomeView && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              mt: 1.5,
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
              onClick={handleNewSection}
              sx={{
                padding: '10px 18px',
                fontSize: '0.9rem',
                fontWeight: 700,
                minHeight: '40px',
                backgroundColor: '#1EEF9F',
                '&:hover': { backgroundColor: '#1AD98A' },
              }}
            >
              New Section
            </Button>
          </Box>
        )}
      </HeaderSection>

      {/* Date Range Selector (solo quando si sta visualizzando/modificando una sezione) */}
      {!isKpiHomeView && (
        <DateSelectorSection>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minWidth: 'fit-content',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Period:
            </Typography>
            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 160 },
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  minHeight: '36px',
                },
              }}
            >
              <Select
                value={dateRangePreset}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                sx={{
                  backgroundColor: 'white',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <MenuItem
                  value="7"
                  disabled={!canUsePresetDays(7)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  Last 7 days
                </MenuItem>
                <MenuItem
                  value="30"
                  disabled={!canUsePresetDays(30)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  Last 30 days
                </MenuItem>
                <MenuItem
                  value="90"
                  disabled={!canUsePresetDays(90)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  Last 90 days
                </MenuItem>
                <MenuItem
                  value="this_month"
                  disabled={!canUseThisMonthPreset}
                  sx={{ fontSize: '0.875rem' }}
                >
                  This month
                </MenuItem>
                <MenuItem value="custom" sx={{ fontSize: '0.875rem' }}>
                  Custom range
                </MenuItem>
              </Select>
            </FormControl>
            {periodSummaryLabel && (
              <Chip
                size="small"
                label={periodSummaryLabel}
                sx={{
                  borderRadius: 1,
                  fontSize: 12,
                  border: '1px solid',
                  borderColor: `${palette.primary.main}35`,
                  bgcolor: `${palette.primary.main}12`,
                  color: palette.primary.main,
                  fontWeight: 600,
                }}
              />
            )}

            {showCustomDateRange && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={tempCustomDates?.from ?? dateRange.from}
                  onChange={(e) => {
                    const newFrom = e.target.value;
                    const currentTo = tempCustomDates?.to ?? dateRange.to;
                    if (currentTo && newFrom > currentTo) {
                      setTempCustomDates({ from: newFrom, to: newFrom });
                    } else {
                      setTempCustomDates({
                        from: newFrom,
                        to: tempCustomDates?.to ?? dateRange.to,
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                    max: (tempCustomDates?.to ?? dateRange.to) || undefined,
                  }}
                  sx={{
                    width: { xs: '100%', sm: 150 },
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      minHeight: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={tempCustomDates?.to ?? dateRange.to}
                  onChange={(e) => {
                    const newTo = e.target.value;
                    const currentFrom = tempCustomDates?.from ?? dateRange.from;
                    if (currentFrom && newTo < currentFrom) {
                      setTempCustomDates({ from: newTo, to: newTo });
                    } else {
                      setTempCustomDates({
                        from: tempCustomDates?.from ?? dateRange.from,
                        to: newTo,
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: (tempCustomDates?.from ?? dateRange.from) || undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    width: { xs: '100%', sm: 150 },
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      minHeight: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={handleApplyCustomDates}
                  sx={{
                    minHeight: '36px',
                  }}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mt: showCustomDateRange ? 1.5 : 0,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minWidth: 'fit-content',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Compare:
            </Typography>
            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 160 },
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  minHeight: '36px',
                },
              }}
            >
              <Select
                value={compareMode}
                onChange={(e) => handleCompareModeChange(e.target.value)}
                sx={{
                  backgroundColor: 'white',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <MenuItem value="none" sx={{ fontSize: '0.875rem' }}>
                  None
                </MenuItem>
                <MenuItem
                  value="previous_period"
                  disabled={!canUsePreviousPeriodCompare}
                  sx={{ fontSize: '0.875rem' }}
                >
                  Previous period
                </MenuItem>
                <MenuItem
                  value="previous_year"
                  disabled={!canUsePreviousYearCompare}
                  sx={{ fontSize: '0.875rem' }}
                >
                  Previous year
                </MenuItem>
                <MenuItem value="custom" sx={{ fontSize: '0.875rem' }}>
                  Custom range
                </MenuItem>
              </Select>
            </FormControl>
            {compareSummaryLabel && (
              <Chip
                size="small"
                label={compareSummaryLabel}
                sx={{
                  borderRadius: 1,
                  fontSize: 12,
                  border: '1px solid',
                  borderColor: `${palette.primary.main}30`,
                  bgcolor: `${palette.primary.main}10`,
                  color: palette.primary.main,
                  fontWeight: 600,
                }}
              />
            )}

            {showCustomCompareDates && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={
                    tempCustomCompareDates?.from ?? compareDateRange?.from ?? ''
                  }
                  onChange={(e) => {
                    const newFrom = e.target.value;
                    const currentTo =
                      tempCustomCompareDates?.to ?? compareDateRange?.to;
                    if (currentTo && newFrom > currentTo) {
                      setTempCustomCompareDates({ from: newFrom, to: newFrom });
                    } else {
                      setTempCustomCompareDates({
                        from: newFrom,
                        to:
                          tempCustomCompareDates?.to ??
                          compareDateRange?.to ??
                          '',
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                    max:
                      (tempCustomCompareDates?.to ?? compareDateRange?.to) ||
                      undefined,
                  }}
                  sx={{
                    width: { xs: '100%', sm: 150 },
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      minHeight: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={
                    tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''
                  }
                  onChange={(e) => {
                    const newTo = e.target.value;
                    const currentFrom =
                      tempCustomCompareDates?.from ?? compareDateRange?.from;
                    if (currentFrom && newTo < currentFrom) {
                      setTempCustomCompareDates({ from: newTo, to: newTo });
                    } else {
                      setTempCustomCompareDates({
                        from:
                          tempCustomCompareDates?.from ??
                          compareDateRange?.from ??
                          '',
                        to: newTo,
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min:
                      (tempCustomCompareDates?.from ??
                        compareDateRange?.from ??
                        lookbackMinDate) ||
                      lookbackMinDate ||
                      undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    width: { xs: '100%', sm: 150 },
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      minHeight: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={handleApplyCustomCompareDates}
                  sx={{
                    minHeight: '36px',
                  }}
                >
                  Apply
                </Button>
              </Box>
            )}
            {lookbackDays !== null && lookbackDays !== undefined && (
              <Typography
                variant="caption"
                sx={{
                  width: '100%',
                  color: 'warning.main',
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                {getLookbackPlanLimitCaption(lookbackDays)} Older dates are
                disabled.
              </Typography>
            )}
          </Box>
        </DateSelectorSection>
      )}

      {/* Controls (solo quando si sta visualizzando/modificando una sezione) */}
      {!isKpiHomeView && (
        <ControlsSection>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
              onClick={() => setShowMetricSelector(true)}
              sx={{
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: 600,
                minHeight: '36px',
              }}
            >
              Add Metric
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MenuIcon sx={{ fontSize: '18px' }} />}
              onClick={() => setSectionsDrawerOpen(true)}
              sx={{
                padding: '7px 16px',
                fontSize: '0.875rem',
                fontWeight: 600,
                minHeight: '36px',
              }}
            >
              Saved Sections
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={handleNewSection}
              sx={{
                padding: '7px 16px',
                fontSize: '0.875rem',
                fontWeight: 600,
                minHeight: '36px',
              }}
            >
              New Section
            </Button>
            {/* Pulsante "Save/Update section" sempre disponibile */}
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<SaveIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                // Usa selectedSection come fallback se resolvedSectionToUpdate non è disponibile
                const sectionToUse = resolvedSectionToUpdate || selectedSection;
                if (sectionToUse) {
                  // Se c'è una sezione selezionata, aggiorna direttamente senza dialog
                  // Questo è il comportamento corretto: quando si apre una sezione salvata,
                  // il pulsante mostra "Update section" e aggiorna quella sezione
                  void handleQuickSave(undefined, sectionToUse);
                } else {
                  // Se non c'è una sezione selezionata, apri dialog per creare nuova sezione
                  setSectionName('');
                  setSaveSectionDialogOpen(true);
                }
              }}
              disabled={selectedMetrics.length === 0 || isSavingSection}
              sx={{
                backgroundColor: '#1EEF9F',
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: 600,
                minHeight: '36px',
                '&:hover': { backgroundColor: '#1AD98A' },
                '&.Mui-disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9F9F9F',
                },
              }}
            >
              {/* Mostra "Update section" se c'è una sezione selezionata (aperta o caricata),
                altrimenti mostra "Save section" per creare una nuova sezione */}
              {resolvedSectionToUpdate || selectedSection
                ? 'Update section'
                : 'Save section'}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={showCharts}
                  onChange={(e) => setShowCharts(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  Show Charts
                </Typography>
              }
              sx={{ marginLeft: 0.5 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      aiCallsRemaining >= 5
                        ? '#6366f1'
                        : aiCallsRemaining > 0
                          ? '#f59e0b'
                          : '#ef4444',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {aiCallsRemaining >= 5
                    ? '5 AI credits'
                    : `${aiCallsRemaining}/5 AI credits`}
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1, color: '#000' }}
                      >
                        AI Report Cost: 5 AI Credits
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 0.5, color: '#000' }}
                      >
                        • AI credits are used to generate intelligent KPI insights
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 0.5, color: '#000' }}
                      >
                        • Your remaining credits:{' '}
                        {aiCallsRemaining || 'Loading...'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#000' }}
                      >
                        • Upgrade your plan for more AI interactions
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor:
                        aiCallsRemaining >= 5
                          ? '#6366f1'
                          : aiCallsRemaining > 0
                            ? '#f59e0b'
                            : '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        lineHeight: 1,
                      }}
                    >
                      i
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                onClick={handleGenerateAIReport}
                disabled={isGeneratingReport || aiCallsRemaining < 5}
                sx={{
                  backgroundColor: '#6366f1',
                  color: 'white',
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  minWidth: '140px',
                  boxShadow: '0 3px 12px rgba(99, 102, 241, 0.3)',
                  border: '2px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#4f46e5',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                    border: '2px solid #3730a3',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    color: '#6b7280',
                    boxShadow: 'none',
                  },
                }}
              >
                {isGeneratingReport ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: 'white' }}
                    />
                    Generating...
                  </>
                ) : (
                  'AI Report'
                )}
              </Button>
            </Box>
          </Box>
        </ControlsSection>
      )}

      {/* Filters: Shopify + Markup Kipify (stessi filtri ordini, vedi KPICard) */}
      {(selectedSection || lastKnownSectionId || isCreatingNewSection) &&
        selectedMetrics.length > 0 &&
        shouldLoadKpiFilterOptions &&
        shopifyConnectorId && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width: '100%',
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Button
                size="small"
                variant={kpiShowMoreFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 16,
                      transform: kpiShowMoreFilters ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                }
                onClick={() => setKpiShowMoreFilters((v) => !v)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 1.5,
                  py: 1.1,
                  whiteSpace: 'nowrap',
                  fontSize: '0.8125rem',
                  borderColor:
                    kpiActiveFilterCount > 0 ? 'primary.main' : '#e0e0e0',
                  color: kpiShowMoreFilters ? '#fff' : 'text.secondary',
                  bgcolor: kpiShowMoreFilters ? 'primary.main' : 'white',
                  '&:hover': {
                    bgcolor: kpiShowMoreFilters
                      ? 'primary.light'
                      : 'rgba(0,0,0,0.04)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Filters
                {kpiActiveFilterCount > 0 && (
                  <Chip
                    label={kpiActiveFilterCount}
                    size="small"
                    sx={{
                      ml: 0.75,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: kpiShowMoreFilters
                        ? 'rgba(255,255,255,0.3)'
                        : 'primary.main',
                      color: '#fff',
                    }}
                  />
                )}
              </Button>
              <Tooltip
                placement="bottom-start"
                leaveDelay={300}
                disableInteractive={false}
                title={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: 'inherit', mb: 0.75 }}
                    >
                      Filters
                    </Typography>
                    <Typography
                      component="ul"
                      variant="body2"
                      sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}
                    >
                      <li>
                        Filters apply to Shopify/Magento metrics and to{' '}
                        <strong>Markup</strong> (Kipify), using your Shopify
                        orders.
                      </li>
                      <li>
                        Other Kipify metrics use unfiltered totals; other
                        connectors are unchanged.
                      </li>
                    </Typography>
                  </Box>
                }
                PopperProps={{
                  sx: {
                    zIndex: 1400,
                    '& .MuiTooltip-tooltip': {
                      bgcolor: 'white',
                      color: 'text.primary',
                      p: 2,
                      maxWidth: 320,
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      border: '1px solid rgba(0,0,0,0.08)',
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  aria-label="Filters info"
                >
                  <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Chip filtri attivi + Clear (identico a Inventory) */}
            {kpiActiveFilterCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                {kpiCountryFilter.map((c) => (
                  <Chip
                    key={`kpi-cf-${c}`}
                    label={c}
                    size="small"
                    onDelete={() =>
                      setKpiCountryFilter((p) => p.filter((x) => x !== c))
                    }
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                ))}
                {kpiCustomerTypeFilter && (
                  <Chip
                    label={
                      kpiCustomerTypeFilter === 'First time'
                        ? 'New'
                        : 'Recurring'
                    }
                    size="small"
                    onDelete={() => setKpiCustomerTypeFilter('')}
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                )}
                {kpiTagFilter.map((t) => (
                  <Chip
                    key={`kpi-tf-${t}`}
                    label={t}
                    size="small"
                    onDelete={() =>
                      setKpiTagFilter((p) => p.filter((x) => x !== t))
                    }
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                ))}
                {kpiCollectionFilter.map((c) => (
                  <Chip
                    key={`kpi-col-${c}`}
                    label={c}
                    size="small"
                    onDelete={() =>
                      setKpiCollectionFilter((p) => p.filter((x) => x !== c))
                    }
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                ))}
                {kpiVendorFilter.map((v) => (
                  <Chip
                    key={`kpi-vf-${v}`}
                    label={v}
                    size="small"
                    onDelete={() =>
                      setKpiVendorFilter((p) => p.filter((x) => x !== v))
                    }
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                ))}
                {kpiProductTypeFilter.map((p) => (
                  <Chip
                    key={`kpi-pt-${p}`}
                    label={p}
                    size="small"
                    onDelete={() =>
                      setKpiProductTypeFilter((prev) =>
                        prev.filter((x) => x !== p),
                      )
                    }
                    sx={{ borderRadius: 1, fontSize: 12, height: 26 }}
                  />
                ))}
                <Button
                  size="small"
                  onClick={() => {
                    setKpiCountryFilter([]);
                    setKpiCustomerTypeFilter('');
                    setKpiTagFilter([]);
                    setKpiCollectionFilter([]);
                    setKpiVendorFilter([]);
                    setKpiProductTypeFilter([]);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    color: 'text.secondary',
                    minWidth: 0,
                    px: 0.75,
                  }}
                >
                  Clear
                </Button>
              </Box>
            )}

            <Box sx={{ width: '100%' }}>
              <Collapse in={kpiShowMoreFilters} timeout={200}>
                <Box
                  sx={{
                    width: '100%',
                    mt: 1,
                    mb: 1.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Country
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={kpiCountryOptions}
                      value={kpiCountryFilter}
                      onChange={(_, v) => setKpiCountryFilter(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 0.5, p: 0 }}
                            size="small"
                          />
                          <Typography variant="body2">{option}</Typography>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select countries..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      )}
                      renderTags={() => null}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Customer type
                    </Typography>
                    <Select
                      size="small"
                      fullWidth
                      displayEmpty
                      value={kpiCustomerTypeFilter}
                      onChange={(e) =>
                        setKpiCustomerTypeFilter(e.target.value as string)
                      }
                      sx={{ backgroundColor: 'white' }}
                      renderValue={(v) =>
                        v ? (
                          v === 'First time' ? (
                            'New (first time)'
                          ) : (
                            'Recurring'
                          )
                        ) : (
                          <Typography color="text.disabled">
                            All customers
                          </Typography>
                        )
                      }
                    >
                      <MenuItem value="">All customers</MenuItem>
                      <MenuItem value="First time">New (first time)</MenuItem>
                      <MenuItem value="recurring">Recurring</MenuItem>
                    </Select>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Tag
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={kpiFilterOptions.tags}
                      value={kpiTagFilter}
                      onChange={(_, v) => setKpiTagFilter(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 0.5, p: 0 }}
                            size="small"
                          />
                          <Typography variant="body2">{option}</Typography>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          placeholder="Select tags..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      )}
                      renderTags={() => null}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Collection
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={kpiFilterOptions.collections}
                      value={kpiCollectionFilter}
                      onChange={(_, v) => setKpiCollectionFilter(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 0.5, p: 0 }}
                            size="small"
                          />
                          <Typography variant="body2">{option}</Typography>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          placeholder="Select collections..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      )}
                      renderTags={() => null}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Vendor
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={kpiFilterOptions.vendors}
                      value={kpiVendorFilter}
                      onChange={(_, v) => setKpiVendorFilter(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 0.5, p: 0 }}
                            size="small"
                          />
                          <Typography variant="body2">{option}</Typography>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          placeholder="Select vendors..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      )}
                      renderTags={() => null}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                    >
                      Product Type
                    </Typography>
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={kpiFilterOptions.productTypes}
                      value={kpiProductTypeFilter}
                      onChange={(_, v) => setKpiProductTypeFilter(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 0.5, p: 0 }}
                            size="small"
                          />
                          <Typography variant="body2">{option}</Typography>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          placeholder="Select types..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      )}
                      renderTags={() => null}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        )}

      {/* Metric Selector Modal */}
      {showMetricSelector && (
        <MetricSelector
          open={showMetricSelector}
          onClose={() => setShowMetricSelector(false)}
          onSelectMetric={handleAddMetric}
          availableMetrics={availableMetrics}
        />
      )}

      {/* KPI Cards Grid with Drag and Drop – stile Custom Report (slot = box di card) */}
      {/* Logica di visualizzazione:
          1. Se c'è una sezione attiva O si sta creando una nuova sezione con metriche → mostra le KPI cards
          2. Altrimenti se ci sono sezioni salvate → mostra griglia sezioni per scegliere
          3. Altrimenti → mostra empty state per creare la prima sezione */}
      {(selectedSection || lastKnownSectionId || isCreatingNewSection) &&
      selectedMetrics.length > 0 ? (
        <KPIGrid>
          {selectedMetrics.map((selectedMetric) => (
            <Box
              key={selectedMetric.id}
              data-column-id={selectedMetric.id}
              className={getColumnClassName(selectedMetric.id)}
              onDragOver={(event) => handleDragOver(event, selectedMetric.id)}
              onDragLeave={(event) => handleDragLeave(event, selectedMetric.id)}
              onDrop={(event) => handleDrop(event, selectedMetric.id)}
              sx={{
                position: 'relative',
                borderRadius: 2,
                // Un solo “chrome” di bordo: quello sulla KPICard. Qui bordo solo mentre trascini
                // sulla drop zone (evita doppio filo grigio slot + card).
                border:
                  dragState.dropZoneId === selectedMetric.id
                    ? '2px dashed rgba(30, 237, 159, 0.9)'
                    : '2px solid transparent',
                backgroundColor:
                  dragState.dropZoneId === selectedMetric.id
                    ? 'rgba(30, 237, 159, 0.06)'
                    : 'transparent',
                transition: 'border 0.15s ease, background-color 0.15s ease',
                boxSizing: 'border-box',
              }}
            >
              <KPICard
                id={selectedMetric.id}
                index={columnOrder.indexOf(selectedMetric.id)}
                metric={selectedMetric.metric}
                connectorType={selectedMetric.connectorType}
                dateRange={dateRange}
                compareDateRange={
                  compareMode !== 'none' ? compareDateRange : null
                }
                showChart={selectedMetric.showChart && showCharts}
                filters={kpiFilters}
                target={selectedMetric.target}
                onRemove={() => handleRemoveMetric(selectedMetric.id)}
                onToggleChart={() => handleToggleChart(selectedMetric.id)}
                onSaveTarget={(target) =>
                  handleSaveMetricTarget(selectedMetric.id, target)
                }
                onDeleteTarget={() =>
                  handleDeleteMetricTarget(selectedMetric.id)
                }
                // Passo gli handler per il drag dalla “maniglia”
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrag={handleDrag}
              />
            </Box>
          ))}
        </KPIGrid>
      ) : isCreatingNewSection ? (
        /* Creazione nuova sezione: mostra empty state editor, non la griglia delle sezioni */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '320px',
            gap: 1.5,
            backgroundColor: 'white',
            borderRadius: 2,
            padding: 3,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: 'rgba(30, 239, 159, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <DashboardIcon sx={{ fontSize: 32, color: '#1EEF9F' }} />
          </Box>
          <Typography
            variant="h6"
            color="text.primary"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Add your first metric
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              mb: 1,
              textAlign: 'center',
              maxWidth: 420,
            }}
          >
            Choose the metrics you want to track in this section. You can save
            it once you have at least one metric.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
            onClick={() => setShowMetricSelector(true)}
            sx={{
              padding: '8px 20px',
              fontSize: '0.875rem',
              fontWeight: 700,
              minHeight: '36px',
            }}
          >
            Add Metric
          </Button>
        </Box>
      ) : orderedKpiSections.length > 0 ? (
        /* Schermata iniziale: mostra le sezioni salvate */
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'text.primary',
              mb: 2,
            }}
          >
            Your Saved Sections
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.82rem', mb: 1.5 }}
          >
            Drag and drop cards to reorder them.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 2,
            }}
          >
            {orderedKpiSections.map((section: KpiSection) => (
              <Card
                key={section.id}
                draggable
                onDragStart={(event) =>
                  handleSectionDragStart(event, section.id)
                }
                onDragOver={(event) => handleSectionDragOver(event, section.id)}
                onDrop={(event) => handleSectionDrop(event, section.id)}
                onDragEnd={handleSectionDragEnd}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow:
                    sectionDropTargetId === section.id
                      ? '0 8px 22px rgba(30, 239, 159, 0.22)'
                      : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease-in-out',
                  border:
                    sectionDropTargetId === section.id
                      ? '1px solid #1EEF9F'
                      : '1px solid rgba(226, 232, 240, 0.8)',
                  position: 'relative',
                  cursor: draggedSectionId === section.id ? 'grabbing' : 'grab',
                  opacity: draggedSectionId === section.id ? 0.55 : 1,
                  '&:hover': {
                    boxShadow:
                      sectionDropTargetId === section.id
                        ? '0 8px 22px rgba(30, 239, 159, 0.22)'
                        : '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-2px)',
                    borderColor: '#1EEF9F',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <Box
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    display: 'flex',
                    gap: 0.25,
                    alignItems: 'center',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="Rename section">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRenameSection(section);
                      }}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete section">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSection(section.id);
                      }}
                      size="small"
                      color="error"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <CardActionArea
                  onClick={() => handleSectionCardClick(section)}
                  sx={{
                    height: '100%',
                    cursor:
                      draggedSectionId === section.id ? 'grabbing' : 'grab',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(30, 239, 159, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <DashboardIcon
                          sx={{ fontSize: 22, color: '#1EEF9F' }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: 'text.primary',
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {section.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          Updated{' '}
                          {new Date(section.updatedAt).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            },
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        label={`${section.config?.selectedMetrics?.length || 0} metrics`}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                          fontWeight: 500,
                        }}
                      />
                      {section.config?.dateRangePreset && (
                        <Chip
                          label={
                            section.config.dateRangePreset === 'this_month'
                              ? 'This Month'
                              : section.config.dateRangePreset === 'custom'
                                ? 'Custom Range'
                                : `Last ${section.config.dateRangePreset} days`
                          }
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            backgroundColor: 'rgba(30, 239, 159, 0.1)',
                            color: '#059669',
                            fontWeight: 500,
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      ) : (
        /* Empty state: nessuna sezione salvata */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '320px',
            gap: 1.5,
            backgroundColor: 'white',
            borderRadius: 2,
            padding: 3,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: 'rgba(30, 239, 159, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <DashboardIcon sx={{ fontSize: 32, color: '#1EEF9F' }} />
          </Box>
          <Typography
            variant="h6"
            color="text.primary"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Create your first KPI section
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              mb: 1,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            KPI sections let you save and organize your most important metrics.
            Start by adding metrics and then save your section.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
            onClick={() => setShowMetricSelector(true)}
            sx={{
              padding: '8px 20px',
              fontSize: '0.875rem',
              fontWeight: 600,
              minHeight: '36px',
            }}
          >
            Add Your First Metric
          </Button>
        </Box>
      )}

      {/* Drawer per le sezioni salvate */}
      <Drawer
        anchor="right"
        open={sectionsDrawerOpen}
        onClose={() => setSectionsDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Saved Sections</Typography>
            <IconButton
              onClick={() => setSectionsDrawerOpen(false)}
              size="small"
            >
              ×
            </IconButton>
          </Box>
          <Button
            variant="contained"
            size="small"
            fullWidth
            startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
            onClick={handleNewSection}
            sx={{
              mb: 2,
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: 600,
              minHeight: '36px',
            }}
          >
            New Section
          </Button>
          {sectionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : savedSections.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No saved sections
              </Typography>
            </Box>
          ) : (
            <List>
              {orderedKpiSections.map((section: KpiSection) => (
                <ListItem
                  key={section.id}
                  secondaryAction={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        mr: -0.5,
                      }}
                    >
                      <Tooltip title="Rename section">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRenameSection(section);
                          }}
                          aria-label="Rename section"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete section">
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteSection(section.id)}
                          color="error"
                          aria-label="Delete section"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  disablePadding
                >
                  <ListItemButton
                    onClick={() => handleLoadSection(section)}
                    selected={selectedSection?.id === section.id}
                  >
                    <ListItemText
                      primary={section.name}
                      secondary={new Date(section.updatedAt).toLocaleDateString(
                        'it-IT',
                      )}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={renameSectionDialogOpen}
        onClose={() => {
          if (!isRenamingSection) {
            setRenameSectionDialogOpen(false);
            setSectionPendingRename(null);
            setRenameNameDraft('');
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: `1px solid ${palette.grey[200]}`,
            boxShadow: `0 24px 48px -12px ${palette.grey[500]}`,
            overflow: 'hidden',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: palette.grey[600],
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.0625rem',
            letterSpacing: '-0.02em',
            color: palette.text.primary,
            pb: 1,
            pt: 2.5,
            px: 3,
            borderBottom: `1px solid ${palette.grey[200]}`,
          }}
        >
          Rename section
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: palette.text.secondary, mb: 2, lineHeight: 1.5 }}
          >
            This name appears on your saved section cards and in the list.
          </Typography>
          <TextField
            autoFocus
            margin="none"
            label="Section name"
            fullWidth
            variant="outlined"
            value={renameNameDraft}
            onChange={(e) => setRenameNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                renameNameDraft.trim() &&
                !isRenamingSection
              ) {
                e.preventDefault();
                void handleConfirmRenameSection();
              }
            }}
            color="primary"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: palette.grey[200],
                transition:
                  'background-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  bgcolor: palette.secondary.main,
                },
                '&.Mui-focused': {
                  bgcolor: palette.background.paper,
                  boxShadow: `0 0 0 3px ${palette.primary.light}`,
                },
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary.main,
                  borderWidth: 1,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: palette.text.secondary,
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            pt: 2,
            gap: 1,
            justifyContent: 'flex-end',
            borderTop: `1px solid ${palette.grey[200]}`,
            bgcolor: palette.grey[200],
          }}
        >
          <Button
            onClick={() => {
              setRenameSectionDialogOpen(false);
              setSectionPendingRename(null);
              setRenameNameDraft('');
            }}
            disabled={isRenamingSection}
            sx={{
              color: palette.text.secondary,
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
              '&:hover': { bgcolor: palette.action.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void handleConfirmRenameSection()}
            disabled={!renameNameDraft.trim() || isRenamingSection}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              borderRadius: 2,
              minWidth: 96,
              bgcolor: palette.primary.main,
              color: palette.primary.contrastText,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: palette.primary.light,
                color: palette.text.primary,
                boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
              },
              '&.Mui-disabled': {
                bgcolor: palette.action.disabledBackground,
                color: palette.text.disabled,
              },
            }}
          >
            {isRenamingSection ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per salvare sezione */}
      <Dialog
        open={saveSectionDialogOpen}
        onClose={() => {
          setSaveSectionDialogOpen(false);
          setSectionName('');
          // Non resettare selectedSection se stiamo aggiornando una sezione esistente
          // Resetta solo se stiamo creando una nuova sezione
          if (!selectedSection) {
            setSelectedSection(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedSection ? 'Update Section' : 'Save Section'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Section Name"
            fullWidth
            variant="outlined"
            value={sectionName || (selectedSection ? selectedSection.name : '')}
            onChange={(e) => setSectionName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              setSaveSectionDialogOpen(false);
              setSectionName('');
              // Non resettare selectedSection se stiamo aggiornando una sezione esistente
              // Resetta solo se stiamo creando una nuova sezione
              if (!selectedSection) {
                setSelectedSection(null);
              }
            }}
            sx={{
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: 600,
              minHeight: '36px',
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSaveSection}
            variant="contained"
            disabled={!sectionName.trim() || isSavingSection}
            sx={{
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: 600,
              minHeight: '36px',
            }}
          >
            {selectedSection ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Report Modal */}
      <Dialog
        open={!!aiReport}
        onClose={() => setAiReport(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AI KPI Report</DialogTitle>
        <DialogContent>
          {aiReport && (
            <div
              dangerouslySetInnerHTML={{ __html: aiReport }}
              ref={(node) => {
                if (node) {
                  // Aggiungi event listener per il pulsante PDF dopo che il contenuto è renderizzato
                  const pdfButton = node.querySelector('#download-pdf-btn');
                  if (pdfButton) {
                    pdfButton.addEventListener('click', downloadAsPDF);
                  }
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiReport(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KPI;
